import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'yingshiju');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryVideos = videos.filter((v) => v.category === activeCategory);

  const sortedVideos = [...categoryVideos].sort((a, b) => {
    if (sortBy === 'hot') return b.views - a.views;
    if (sortBy === 'new') return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
    return b.rating - a.rating;
  });

  const webPageLD = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'} - 国产视频大全`,
    description: currentCategory?.description || '',
    url: `https://www.guochanshipin.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '国产视频大全',
      url: 'https://www.guochanshipin.com/',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageLD)}</script>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="pt-20 pb-6 md:pb-8 bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/yingshiju' },
                { label: currentCategory?.name || '' },
              ]}
            />
            <div className="mt-4">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {currentCategory?.name || '视频分类'}
              </h1>
              <p className="text-sm md:text-base text-foreground-600">{currentCategory?.description}</p>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-8">
          <div className="sticky top-16 z-30 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 max-w-7xl mx-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">共 <strong className="text-foreground-700">{categoryVideos.length}</strong> 部视频</p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[
                { key: 'hot', label: '最热' },
                { key: 'new', label: '最新' },
                { key: 'rating', label: '评分' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key as typeof sortBy)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    sortBy === s.key
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {sortedVideos.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-video-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无视频内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {sortedVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}